import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { PricingRule, PricingRules, Product } from "utils/types/ProductTypes";
import { updateProductPricing } from "utils/api/private/ProductApiService";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  product: Product | null;
}

const defaultPricing: PricingRules = {
  baseMargin: 0.15,
  fixedFee: 2,
  tiers: [],
};

export const PricingDialog = ({ open, onClose, onSave, product }: Props) => {
  const [pricing, setPricing] = useState<PricingRules>(defaultPricing);

  useEffect(() => {
    if (product?.pricing) {
      setPricing(product.pricing);
    } else if (open) {
      setPricing(defaultPricing);
    }
  }, [product, open]);

  const handleTierChange = (
    index: number,
    field: keyof PricingRule,
    value: string
  ) => {
    const updatedTiers = [...(pricing.tiers || [])];
    updatedTiers[index] = {
      ...updatedTiers[index],
      [field]: parseFloat(value) || 0,
    };
    setPricing((prev) => ({ ...prev, tiers: updatedTiers }));
  };

  const addTier = () => {
    const newTiers = [
      ...(pricing.tiers || []),
      { from: 0, margin: 0, fixedFee: 0 },
    ];
    setPricing((prev) => ({ ...prev, tiers: newTiers }));
  };

  const removeTier = (index: number) => {
    const newTiers = [...(pricing.tiers || [])];
    newTiers.splice(index, 1);
    setPricing((prev) => ({ ...prev, tiers: newTiers }));
  };

  const handleSave = async () => {
    if (!product) return;

    const clean = {
      baseMargin: Number.isFinite(pricing.baseMargin) ? pricing.baseMargin : 0,
      fixedFee: Number.isFinite(pricing.fixedFee) ? pricing.fixedFee : 0,
      tiers: (pricing.tiers || []).map((t) => ({
        from: Number.isFinite(t.from) ? t.from : 0,
        margin: Number.isFinite(t.margin) ? t.margin : 0, // already ratio
        fixedFee: Number.isFinite(t.fixedFee) ? t.fixedFee : 0,
      })),
    };

    try {
      await updateProductPricing(product.id, clean);
      onSave();
      onClose();
    } catch (e) {
      console.error("Failed to update pricing:", e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Pricing for {product?.name}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              label="Base Margin (%)"
              type="number"
              value={pricing.baseMargin * 100}
              onChange={(e) =>
                setPricing((p) => ({
                  ...p,
                  baseMargin: parseFloat(e.target.value) / 100 || 0,
                }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Base Fixed Fee ($)"
              type="number"
              value={pricing.fixedFee}
              onChange={(e) =>
                setPricing((p) => ({
                  ...p,
                  fixedFee: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            mb: 1,
          }}
        >
          <Typography variant="h6">Pricing Tiers</Typography>
          <Button startIcon={<AddIcon />} onClick={addTier}>
            Add Tier
          </Button>
        </Box>

        {pricing.tiers?.map((tier, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <TextField
                  label="From ($)"
                  type="number"
                  value={tier.from}
                  onChange={(e) =>
                    handleTierChange(index, "from", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Margin (%)"
                  type="number"
                  value={tier.margin * 100}
                  onChange={(e) =>
                    handleTierChange(
                      index,
                      "margin",
                      String(parseFloat(e.target.value) / 100)
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Fixed Fee ($)"
                  type="number"
                  value={tier.fixedFee}
                  onChange={(e) =>
                    handleTierChange(index, "fixedFee", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => removeTier(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Pricing
        </Button>
      </DialogActions>
    </Dialog>
  );
};
